import { PERMISSIONS, type Permission } from './permissions';
import type { ScopeKind } from './scope';

/**
 * Roles — named bundles of permissions, each belonging to exactly one scope
 * kind.
 *
 * A role name never appears in two scope kinds. That is what makes `clerk` at
 * Sussex and `clerk` at Riverview two grants of one role rather than one
 * ambiguous string, and it is the property the old flat `app_user.roles` column
 * could not have.
 */

/** Munifocus staff, in the admin portal. */
export const PLATFORM_ROLES = [
  'platform_admin',
  'platform_editor',
  'platform_support',
  'platform_viewer',
] as const;
export type PlatformRole = (typeof PLATFORM_ROLES)[number];

/**
 * Municipal identities. `gov_officer` is granted by `officer_appointment`
 * rather than `government_membership` — it carries statutory authorities and a
 * badge number, so it is an appointment, not a seat.
 *
 * **There is deliberately no `council` role.** Council is the elected body, not
 * a set of accounts: its members are recorded on the local government profile
 * (`council_member`) and none of them log in. Council still binds the
 * municipality — it is the only body that can — but it does so by *resolution*,
 * and the clerk records that resolution in the portal. See `clerk` below.
 */
export const GOVERNMENT_ROLES = [
  'counsel',
  'clerk',
  'finance',
  'gov_supervisor',
  'gov_officer',
  'gov_staff',
  'gov_observer',
] as const;
export type GovernmentRole = (typeof GOVERNMENT_ROLES)[number];

/** Roles issued by `government_membership` (everything but the appointment). */
export const GOVERNMENT_MEMBERSHIP_ROLES = [
  'counsel',
  'clerk',
  'finance',
  'gov_supervisor',
  'gov_staff',
  'gov_observer',
] as const;
export type GovernmentMembershipRole = (typeof GOVERNMENT_MEMBERSHIP_ROLES)[number];

/** Enforcement-services contractors. */
export const COMPANY_ROLES = ['company_admin', 'company_supervisor', 'company_officer'] as const;
export type CompanyRole = (typeof COMPANY_ROLES)[number];

/** Per-file team seats. Narrower than the municipality that owns the file. */
export const FILE_ROLES = [
  'lead_officer',
  'assisting_officer',
  'file_clerk',
  'file_counsel',
  'file_supervisor',
  'observer',
] as const;
export type FileRole = (typeof FILE_ROLES)[number];

/** The owner/occupant view on the public portal. */
export const CITIZEN_ROLES = ['file_participant'] as const;
export type CitizenRole = (typeof CITIZEN_ROLES)[number];

export const ROLES = [
  ...PLATFORM_ROLES,
  ...GOVERNMENT_ROLES,
  ...COMPANY_ROLES,
  ...FILE_ROLES,
  ...CITIZEN_ROLES,
] as const;
export type Role = (typeof ROLES)[number];

/** Which scope kind each role is held at. */
export const ROLE_SCOPE: Record<Role, ScopeKind> = {
  platform_admin: 'platform',
  platform_editor: 'platform',
  platform_support: 'platform',
  platform_viewer: 'platform',

  counsel: 'local_government',
  clerk: 'local_government',
  finance: 'local_government',
  gov_supervisor: 'local_government',
  gov_officer: 'local_government',
  gov_staff: 'local_government',
  gov_observer: 'local_government',

  company_admin: 'company',
  company_supervisor: 'company',
  company_officer: 'company',

  lead_officer: 'file',
  assisting_officer: 'file',
  file_clerk: 'file',
  file_counsel: 'file',
  file_supervisor: 'file',
  observer: 'file',

  file_participant: 'citizen',
};

/**
 * The coded default policy. Administrator edits layer on top of this via
 * `role_permission_grant`; no row means the default applies. Same
 * defaults-in-code / overrides-in-database split the old `MODULE_ROLES` +
 * `role_module_override` pair used, with a second dimension and a tenant.
 *
 * Two rules to read it by:
 *
 * 1. A role's permissions apply *within its own scope*. `clerk` holding
 *    `file:read` means every file at that municipality; `lead_officer` holding
 *    it means that one file.
 *
 * 2. `gov_officer` deliberately does **not** carry `file:read`. An officer sees
 *    the files they are on a team for, which is what "manage users to files"
 *    means, and it is the difference between an outside contractor seeing their
 *    own caseload and seeing a municipality's entire enforcement history. A
 *    municipality that wants officers to see the whole queue grants
 *    `gov_officer → file:read` in the access-control matrix; it is one row.
 */
export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  // ── Platform ────────────────────────────────────────────────────────────
  platform_admin: PERMISSIONS,
  platform_editor: ['content:manage', 'law:read', 'audit:read'],
  platform_support: [
    'support:manage',
    'enrollment:manage',
    'calendar:manage',
    'law:read',
    'audit:read',
  ],
  platform_viewer: ['law:read', 'audit:read'],

  // ── Local government ────────────────────────────────────────────────────
  // The solicitor: drafts what council resolves and the clerk records, and owns
  // the escalation path. Holds
  // `file:rollback` because correcting a wrongly-advanced legal record is a
  // legal call, and holds no signing power over contracts.
  counsel: [
    'contract:read',
    'contract:draft',
    'officer:read',
    'member:read',
    'file:read',
    'file:public_read',
    'file:rollback',
    'signing:read',
    'law:read',
    'law:manage',
    'audit:read',
    'request:read',
    'request:submit',
    'request:handle',
    // Disputed tickets end up on the solicitor's desk.
    'ticket:read',
    // Reads the debt it may have to defend, and certifies it as a lien.
    'cost:read',
    'cost:escalate',
    'inspection:read',
    // Counsel prepares the record that goes to court and before the committee.
    'court_package:export',
    'appeal:manage',
    'ai:assist',
    'analytics:read',
  ],
  // The clerk is the municipality's corporate officer, and the only account
  // through which council's decisions enter the system. Council resolves; the
  // clerk certifies and records. That is why every power the elected body holds
  // — binding a contract, appointing an officer — is exercised here, against a
  // resolution reference, rather than by a councillor logging in.
  //
  // The control on that power is therefore *not* a second account. It is the
  // resolution: `company_contract.council_resolution_ref` and
  // `officer_appointment.council_resolution_ref` record which decision
  // authorised the act, and the audit log records who typed it.
  clerk: [
    'contract:read',
    'contract:draft',
    'contract:sign',
    'contract:terminate',
    'company:appoint',
    'company:revoke',
    'officer:read',
    'officer:appoint',
    'officer:revoke',
    'member:read',
    'member:appoint',
    'member:revoke',
    'file:read',
    'file:public_read',
    'file:create',
    'file:team:read',
    'file:team:manage',
    'document:draft',
    'signing:read',
    'signing:grant',
    'signing:revoke',
    'citizen:grant',
    'citizen:revoke',
    'law:read',
    'audit:read',
    'request:read',
    'request:submit',
    'request:handle',
    'report:read',
    'report:triage',
    // The clerk keeps the municipality's fine schedule — it is set by by-law —
    // and settles tickets that come across the counter.
    'ticket:read',
    'ticket:manage',
    'fine_schedule:manage',
    // The corporate officer: approves what is billed, records what is paid and
    // sends what is not to the tax roll.
    'cost:read',
    'cost:record',
    'cost:approve',
    'cost:payment',
    'cost:escalate',
    // The clerk's office is where licences are issued from and where the
    // municipality decides what it licenses and what its checklists ask.
    'permit:read',
    'permit:review',
    'permit:configure',
    'inspection:read',
    'inspection:schedule',
    'inspection:configure',
    'invoice:read',
    // Secretary to the Appeal Committee: receives the appeal, sets the hearing,
    // and records the decision the committee reached.
    'court_package:export',
    'appeal:manage',
    'appeal:decide',
    'analytics:read',
    // The clerk keeps the municipality's by-law register, so the citations the
    // workflows print — by-law numbers above all — are the clerk's to set.
    'workflow:configure',
    // The counter: calls, walk-ins and emails, and the callbacks they need.
    'frontdesk:log',
    // The municipal administrator's desk: the dashboard, the Form 4 name
    // approval and council's authorization of remedial work. A Deputy or
    // Assistant Clerk holds the clerk seat under their own title, so they hold
    // these too.
    'clerk_dashboard:read',
    'form4:approve',
    'council_authorization:record',
  ],
  // Finance sees what is owed and collected, and records payments taken in
  // person; it issues no ticket and edits no fine schedule. It is also the seat
  // cost recovery was written for: it approves the costs, issues the statement,
  // banks the payments and escalates the arrears. It reads the permit queue for
  // the fees recorded against it.
  finance: [
    'file:read',
    'file:public_read',
    'law:read',
    'audit:read',
    'request:read',
    'ticket:read',
    'ticket:manage',
    'cost:read',
    'cost:record',
    'cost:approve',
    'cost:payment',
    'cost:escalate',
    'permit:read',
    'invoice:read',
    'analytics:read',
  ],
  gov_supervisor: [
    'officer:read',
    'member:read',
    'file:read',
    'file:public_read',
    'file:create',
    'file:update',
    'file:advance',
    'file:rollback',
    'file:team:read',
    'file:team:manage',
    'evidence:add',
    'document:draft',
    'document:publish',
    'citizen:grant',
    'citizen:revoke',
    'signing:read',
    'law:read',
    'audit:read',
    'request:read',
    'request:submit',
    'request:handle',
    // The supervisor is who watches the team map, and goes on duty themselves.
    // Deliberately not given to a contractor's supervisor: the map shows every
    // officer on duty at the town, not only that contractor's own. A
    // municipality that wants it grants it in the access matrix.
    'tracking:report',
    'tracking:view',
    // Public reports: the supervisor decides what becomes of them like any
    // officer, and sees the whole municipality's queue.
    'report:read',
    'report:triage',
    'ticket:read',
    'ticket:issue',
    'ticket:manage',
    // Records and approves the work the team ordered; money is finance's.
    'cost:read',
    'cost:record',
    'cost:approve',
    // Reviews applications, runs the inspection calendar and inspects.
    'permit:read',
    'permit:review',
    'inspection:read',
    'inspection:schedule',
    'inspection:perform',
    'inspection:configure',
    // Contractor operations, where the municipality has them on: dispatching
    // the team, recording their own time and approving the team's.
    'dispatch:manage',
    'time:report',
    'time:approve',
    'court_package:export',
    'appeal:manage',
    'appeal:decide',
    // The supervisor runs the caseload: the assistant on any file there, and
    // the municipality's numbers.
    'ai:assist',
    'analytics:read',
    // Answers the team's safety alerts, and goes out alone too.
    'safety:checkin',
    'safety:respond',
    'frontdesk:log',
  ],
  // See rule 2 above: no municipality-wide `file:read`. `tracking:report` lets
  // an officer go on duty — it shares a position only while they say so, and
  // only where the municipality has turned live tracking on.
  //
  // `report:*` because the appointed officer — usually the contractor's — is
  // who works the public report queue: they could open a file from a report,
  // so they may also fold one into a file or decline it.
  //
  // `inspection:read` shows the calendar; `inspection:perform` lets them record
  // results, and only on an inspection assigned to them. No `permit:read`: the
  // inspection carries what the inspector needs to know about its permit.
  gov_officer: [
    'file:create',
    'officer:read',
    'signing:read',
    'law:read',
    'tracking:report',
    'report:read',
    'report:triage',
    'inspection:read',
    'inspection:perform',
    'time:report',
    // Every officer can run a check-in timer and raise an SOS, wherever the
    // municipality has officer safety on.
    'safety:checkin',
    // Officers take calls in the field too, and return the callbacks assigned
    // to them.
    'frontdesk:log',
  ],
  // Receives files routed out for external action. Read-only, municipality-wide
  // — this is the old `access_type = 'staff'`, unchanged in substance.
  gov_staff: [
    'file:read',
    'file:public_read',
    'law:read',
    'request:read',
    'permit:read',
    'inspection:read',
  ],
  // A councillor, the mayor or another official who should be able to look but
  // never act: every file read-only, and who holds what at the municipality.
  // At a limited-licence municipality the ceiling narrows the files to the
  // public view, like every other seat there.
  gov_observer: [
    'file:read',
    'file:public_read',
    'member:read',
    'officer:read',
    'law:read',
    'request:read',
    'permit:read',
    'inspection:read',
    // A councillor's question is usually "how are we doing?" — the dashboard
    // answers it without opening anything.
    'analytics:read',
  ],

  // ── Company ─────────────────────────────────────────────────────────────
  // MES. Every one of these is bounded to municipalities the company holds a
  // live contract with, by `expandCompanyGrants` — not by this list.
  company_admin: [
    'contract:read',
    'employee:read',
    'employee:manage',
    'officer:read',
    'officer:propose',
    'file:read',
    'file:team:read',
    'file:team:manage',
    'audit:read',
    'request:read',
    'request:submit',
    'request:handle',
    'report:read',
    // The contractor writes the tickets, so it may keep the schedule they are
    // written from — at the municipalities it holds a live contract with.
    'ticket:read',
    'ticket:manage',
    'fine_schedule:manage',
    // The contractor did the work, so it records what the work cost. It never
    // approves its own invoice, and never records a payment.
    'cost:read',
    'cost:record',
    // Runs its own operations: dispatches its officers, approves their time,
    // and invoices the towns it works for.
    'dispatch:manage',
    'time:approve',
    'invoice:manage',
    // The contractor sees each municipality it serves, and compares them by
    // name. Bounded to live contracts like everything else here.
    'analytics:read',
    'analytics:benchmark',
  ],
  company_supervisor: [
    'contract:read',
    'employee:read',
    'officer:read',
    'file:read',
    'file:team:read',
    'audit:read',
    'request:read',
    'request:handle',
    'report:read',
    'ticket:read',
    'ticket:manage',
    'cost:read',
    'cost:record',
    'dispatch:manage',
    'time:approve',
    'analytics:read',
    'analytics:benchmark',
  ],
  // An employee with no appointment yet: they can see the company roster and
  // nothing operational until council appoints them and the clerk records it.
  company_officer: ['employee:read'],

  // ── File ────────────────────────────────────────────────────────────────
  lead_officer: [
    'file:read',
    'file:update',
    'file:advance',
    'file:team:read',
    'evidence:add',
    'document:draft',
    'document:publish',
    'citizen:grant',
    'citizen:revoke',
    'signing:read',
    'ticket:read',
    'ticket:issue',
    'cost:read',
    'cost:record',
    // The lead officer assembles the file for court and records an appeal that
    // reaches them; the committee's decision is the clerk's to enter.
    'court_package:export',
    'appeal:manage',
    'ai:assist',
    'safety:checkin',
  ],
  assisting_officer: [
    'file:read',
    'file:update',
    'file:team:read',
    'evidence:add',
    'document:draft',
    'ticket:read',
    'ticket:issue',
    'cost:read',
    'cost:record',
    'ai:assist',
    'safety:checkin',
  ],
  file_clerk: [
    'file:read',
    'file:team:read',
    'file:team:manage',
    'document:draft',
    'document:publish',
    'citizen:grant',
    'cost:read',
    'cost:record',
    'cost:payment',
    'court_package:export',
    'appeal:manage',
    'appeal:decide',
  ],
  file_counsel: [
    'file:read',
    'file:rollback',
    'file:team:read',
    'document:draft',
    'court_package:export',
    'appeal:manage',
    'ai:assist',
  ],
  file_supervisor: [
    'file:read',
    'file:update',
    'file:advance',
    'file:rollback',
    'file:team:read',
    'file:team:manage',
    'evidence:add',
    'document:draft',
    'document:publish',
    'ticket:read',
    'ticket:issue',
    'ticket:manage',
    'cost:read',
    'cost:record',
    'cost:approve',
    'court_package:export',
    'appeal:manage',
    'appeal:decide',
    'ai:assist',
    'safety:checkin',
  ],
  observer: ['file:read'],

  // ── Citizen ─────────────────────────────────────────────────────────────
  file_participant: ['file:read'],
};

/** The superuser. Never denied, whatever the override rows say. */
export const SUPERUSER_ROLE: Role = 'platform_admin';

const ROLE_SET: ReadonlySet<string> = new Set(ROLES);

export function isRole(value: string): value is Role {
  return ROLE_SET.has(value);
}

/** Every role held at a given scope kind, in display order. */
export function rolesForScope(kind: ScopeKind): Role[] {
  return ROLES.filter((role) => ROLE_SCOPE[role] === kind);
}

/**
 * Municipal seats, printed, in both official languages.
 *
 * These exist because a *server* has to name a seat: the portals translate
 * through `libs/i18n`, but an email is rendered by the messaging worker for a
 * recipient whose language is a column on their account, and an inbox row
 * stores both languages at write time because the reader may switch language
 * afterwards. Neither can reach a React translation table.
 *
 * `gov_officer` is here for completeness even though it is granted by
 * appointment rather than by a seat — the same map should answer for every
 * government role rather than having one hole in it that a caller has to know
 * about.
 */
export const GOVERNMENT_ROLE_LABELS: Record<GovernmentRole, { en: string; fr: string }> = {
  counsel: { en: 'Solicitor', fr: 'Avocat-conseil' },
  clerk: { en: 'Clerk', fr: 'Greffier' },
  finance: { en: 'Finance', fr: 'Finances' },
  gov_supervisor: { en: 'Supervisor', fr: 'Superviseur' },
  gov_officer: { en: 'By-law enforcement officer', fr: 'Agent d’application des règlements' },
  gov_staff: { en: 'Municipal staff', fr: 'Personnel municipal' },
  gov_observer: { en: 'Observer', fr: 'Observateur' },
};

/**
 * Per-file seats, printed, in both official languages. Server-side for the same
 * reason as {@link GOVERNMENT_ROLE_LABELS}: mail is rendered by a worker that
 * cannot reach a React translation table.
 */
export const FILE_ROLE_LABELS: Record<FileRole, { en: string; fr: string }> = {
  lead_officer: { en: 'Lead officer', fr: 'Agent responsable' },
  assisting_officer: { en: 'Assisting officer', fr: 'Agent de soutien' },
  file_clerk: { en: 'File clerk', fr: 'Greffier du dossier' },
  file_counsel: { en: 'File counsel', fr: 'Conseiller juridique du dossier' },
  file_supervisor: { en: 'File supervisor', fr: 'Superviseur du dossier' },
  observer: { en: 'Observer', fr: 'Observateur' },
};

/** Contractor seats, printed, in both official languages. */
export const COMPANY_ROLE_LABELS: Record<CompanyRole, { en: string; fr: string }> = {
  company_admin: { en: 'Company administrator', fr: 'Administrateur d’entreprise' },
  company_supervisor: { en: 'Company supervisor', fr: 'Superviseur d’entreprise' },
  company_officer: { en: 'Company employee', fr: 'Employé de l’entreprise' },
};
